import { ChangeEvent, memo, MouseEventHandler, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@/shared/ui/box';

import * as Styled from './input.styles';
import { InputProps } from './input.types';

// TODO: Remove errorTop props as they are not used in the component
const InputComponent = ({
	label,
	onChange,
	value,
	className,
	variant = 'primary',
	type = 'text',
	Adornment,
	paragraph = '0',
	maxWidth = 'm',
	minWidth = 's',
	display = 'block',
	error = null,
	width = 'full',
	format,
	EndAdornment,
	errorBottom,
	errorTop,
	autoComplete,
	required,
	spellCheck = false,
	...props
}: InputProps) => {
	const errorRef = useRef<HTMLDivElement | null>(null);
	const [dynamicBottom, setDynamicBottom] = useState<string>();

	const hasValue = value !== undefined && value !== null && String(value).length > 0;

	const overrideOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;

			if (format) {
				onChange?.(format(value));
			}
			if (!format) {
				onChange?.(e.target.value);
			}
		},
		[format, onChange],
	);

	const handleRemoveDefault: MouseEventHandler<HTMLLabelElement> = (e) => {
		e.preventDefault();
	};

	useLayoutEffect(() => {
		const height = errorRef.current?.offsetHeight ?? 0;

		setDynamicBottom(`-${height}px`);
	}, [error?.message]);

	const input = useMemo(
		() => (
			<Box width={width} maxWidth={maxWidth} minWidth={minWidth} display={display}>
				<Styled.Input
					paragraph={paragraph}
					adornment={Boolean(Adornment)}
					endAdornment={Boolean(EndAdornment)}
					variant={variant}
					onChange={overrideOnChange}
					value={value}
					error={error}
					type={type}
					autoComplete={autoComplete}
					spellCheck={spellCheck}
					hasValue={hasValue}
					{...props}
				/>
			</Box>
		),
		[
			Adornment,
			display,
			error,
			hasValue,
			maxWidth,
			minWidth,
			overrideOnChange,
			paragraph,
			props,
			type,
			value,
			variant,
			width,
		],
	);

	return (
		<Styled.Wrapper
			className={className}
			onClick={handleRemoveDefault}
			maxWidth={maxWidth}
			variant={variant}
			hasValue={hasValue}
		>
			{label && (
				<Styled.Label
					label={label}
					Component={
						required && (
							<>
								{label}
								<sup>*</sup>
							</>
						)
					}
				/>
			)}
			{Adornment && <Styled.Adornment Svg={Adornment} variant={variant} />}
			{EndAdornment}
			{Boolean(error) && (
				<Box minWidth={minWidth} position="relative" direction="column">
					{input}
					{error?.message && (
						<Styled.ErrorLabel ref={errorRef} bottom={errorBottom ?? dynamicBottom ?? '6px'} top={errorTop}>
							{error.message}
						</Styled.ErrorLabel>
					)}
				</Box>
			)}
			{!error && input}
		</Styled.Wrapper>
	);
};

export default memo(InputComponent);
